import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

export interface SurveyListItem {
  id: string;
  survey_id: string;
  external_id: string;
  survey_name: string;
  survey_version: string;
  question_count: number;
  created_on: string;
  audience_name: string;
  survey_url: string;
  language_urls: { [language: string]: string };
  is_editable: boolean;
}

export interface SurveyAnswer {
  id: string;
  external_key: string;
  text: string;
  sort_order: number;
}

export interface SurveyQuestionDetailResponse {
  id: string;
  display_order: number;
  external_key: string;
  type: string;
  report_display_text: string | null;
  text: string;
  language_id: string;
  language_code: string;
  answers: SurveyAnswer[];
}

export interface CreateSurveyRequest {
  template_survey_id: string;
  audience_id: string;
}

export interface CreateSurveyResponse {
  message: string;
  eoa_id: string;
  audience_id: string;
}

export interface DeleteQuestionResponse {
  message: string;
}

export interface TemplateSurvey {
  id: string;
  name: string;
}

export interface Audience {
  id: string;
  name: string;
}

export const getTemplateSurveys = async (accessToken: string): Promise<TemplateSurvey[]> => {
  const response = await axios.get<TemplateSurvey[]>(`${BASE_URL}/surveys/templates`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  return response.data;
};

export const getAudiences = async (accessToken: string): Promise<Audience[]> => {
  const response = await axios.get<Audience[]>(`${BASE_URL}/surveys/audiences`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  return response.data;
};

export const getSurveys = async (eoaId: string, accessToken: string): Promise<SurveyListItem[]> => {
  const response = await axios.get<SurveyListItem[]>(`${BASE_URL}/eoas/${eoaId}/surveys`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  return response.data;
};

export const createSurvey = async (
  eoaId: string,
  accessToken: string,
  data: CreateSurveyRequest
): Promise<CreateSurveyResponse> => {
  const response = await axios.post<CreateSurveyResponse>(`${BASE_URL}/eoas/${eoaId}/surveys`, data, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  return response.data;
};

export const getSurveyQuestions = async (
  eoaSurveyId: string,
  accessToken: string
): Promise<SurveyQuestionDetailResponse[]> => {
  const response = await axios.get<SurveyQuestionDetailResponse[]>(
    `${BASE_URL}/eoas/surveys/${eoaSurveyId}/questions`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
  return response.data;
};

export const deleteQuestion = async (
  eoaSurveyId: string,
  questionId: string,
  accessToken: string
): Promise<DeleteQuestionResponse> => {
  const response = await axios.delete<DeleteQuestionResponse>(
    `${BASE_URL}/eoas/surveys/${eoaSurveyId}/questions/${questionId}`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
  return response.data;
};

export interface UpdateQuestionTextRequest {
  language_id: string;
  text: string;
  description?: string;
}

export interface UpdateAnswerTextRequest {
  language_id: string;
  text: string;
}

export interface SurveyTextUpdatedMessage {
  eoa_survey_id: string;
  type: 'question_text_updated' | 'answer_text_updated';
  question_id: string;
  answer_id?: string;
  success: boolean;
}

export const updateQuestionText = async (
  eoaSurveyId: string,
  questionId: string,
  data: UpdateQuestionTextRequest,
  accessToken: string
): Promise<void> => {
  await axios.patch(`${BASE_URL}/eoas/surveys/${eoaSurveyId}/questions/${questionId}/text`, data, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

export const updateAnswerText = async (
  eoaSurveyId: string,
  questionId: string,
  answerId: string,
  data: UpdateAnswerTextRequest,
  accessToken: string
): Promise<void> => {
  await axios.patch(`${BASE_URL}/eoas/surveys/${eoaSurveyId}/questions/${questionId}/answers/${answerId}/text`, data, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

export interface SchoolSurveyStats {
  school_id: string;
  school_name: string;
  expected_count: number | null;
  submission_count: number;
}

export interface AudienceSurveyStats {
  total_expected: number | null;
  total_submissions: number;
  per_school: SchoolSurveyStats[];
}

export interface SurveyResponseStatsResponse {
  students: AudienceSurveyStats;
  families: AudienceSurveyStats;
  staff: AudienceSurveyStats;
}

export const getSurveyResponseStats = async (
  eoaId: string,
  accessToken: string
): Promise<SurveyResponseStatsResponse> => {
  const response = await axios.get<SurveyResponseStatsResponse>(`${BASE_URL}/eoa/${eoaId}/survey-response-stats`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
};

export const getSurveyResponseStatsPublic = async (eoaId: string): Promise<SurveyResponseStatsResponse> => {
  const response = await axios.get<SurveyResponseStatsResponse>(`${BASE_URL}/eoa/${eoaId}/survey-response-stats`);
  return response.data;
};
